import React, {useCallback, useContext, useMemo} from 'react';

import {WidthItem} from 'src/components/width-item';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {mapDateFormat} from 'src/utils/mapDateFormat';

import {Calendar} from '@pb/kit-component-calendar';
import {CustomGroupItem} from '@pb/kit-component-input-v2';
import {LocaleContext} from '@pb/kit-context-locale';
import {formatDate} from '@pb/kit-formatters';


type Props = {
    id: string,
    selectRangeType: 'start' | 'end',
    onChange: (date: string) => void,

    label?: React.ReactNode,
    startDate?: string,
    endDate?: string,
    invalid?: boolean,
    clearable?: boolean,
    preventPastDate?: boolean,
    preventFutureDate?: boolean,
    width?: number,
    readonly?: boolean,
    disabled?: boolean,
    onFocus?: () => void,
};

type CustomErrorMessage = {
    date: string,
    selectRangeType: 'start' | 'end',
    isToday: (date: string) => boolean,
    isFutureDate: (date: string) => boolean,
    dateFormat: string,
};

const setCustomErrorMessage = ({date, selectRangeType, isToday, isFutureDate, dateFormat}: CustomErrorMessage) => {
    if (!isFutureDate(date) && !isToday(date)) {
        return `License ${selectRangeType} date cannot be earlier than today`;
    }

    return `Invalid date. Please re-enter the date in the correct format (e.g. ${formatDate(
        new Date(),
        mapDateFormat(dateFormat, 'NUMERIC'),
    )})`;
};

export const DateRangeInput: React.FC<Props> = props => {
    const {
        id,
        selectRangeType,
        onChange,

        invalid,
        label,
        clearable,
        preventPastDate,
        preventFutureDate,
        width,
        readonly,
        disabled,
        onFocus,
    } = props;

    const {isFutureDate, isToday, today, getLocalDate} = useDateTime();
    const startDate = props.startDate ? getLocalDate(props.startDate) : undefined;
    const endDate = props.endDate ? getLocalDate(props.endDate) : undefined;
    const date = selectRangeType === 'start' ? startDate : endDate;
    const {locale} = useContext(LocaleContext);

    const onChangeDate = useCallback((newDate: Date | undefined) => {
        onChange(formatDate(newDate?.toISOString(), 'YYYY-MM-DD'));
    }, [onChange]);

    const content = useMemo(() => {
        let itemWidth;

        if (width) {
            itemWidth = label ? width - 70 : width;
        }

        return (
            <WidthItem width={itemWidth}>
                <Calendar {...{
                    id,
                    selected: date,
                    disabled,
                    readonly,
                    dateFormat: mapDateFormat(locale.dateFormat, 'CALENDAR'),
                    selectsStart: selectRangeType === 'start' ? true : undefined,
                    selectsEnd: selectRangeType === 'end' ? true : undefined,
                    startDate,
                    endDate,
                    setCustomErrorMessage: preventPastDate
                        ? (_: Date, errorDate: string) => setCustomErrorMessage({
                                date: errorDate,
                                selectRangeType,
                                isToday,
                                isFutureDate,
                                dateFormat: locale.dateFormat,
                            }) : undefined,
                    minDate: preventPastDate ? today : undefined,
                    maxDate: preventFutureDate ? today : undefined,
                    isClearable: clearable,
                    onChange: onChangeDate,
                    onFocus,
                }}
                />
            </WidthItem>
        );
    }, [
        id,
        onChangeDate,
        preventFutureDate,
        selectRangeType,
        startDate,
        endDate,
        date,
        label,
        clearable,
        width,
        isToday,
        preventPastDate,
        today,
        isFutureDate,
        readonly,
        disabled,
        onFocus,
        locale.dateFormat,
    ]);

    return label ? (
        <CustomGroupItem label={label} componentType={invalid ? 'ERROR' : undefined}>
            {content}
        </CustomGroupItem>
    ) : content;
};
